/**
 * Admin Users API Client
 *
 * Batch & Bowl specific enrichment for the admin user-management screen
 * (Task 13): OAuth provider badges and last-login timestamps. Better
 * Auth's admin plugin (see apps/api/src/auth.ts) never returns these —
 * its output allow-lists by declared schema field, so app-specific
 * columns like `lastLoginAt` are stripped, and there is no Better Auth
 * admin endpoint at all for "list accounts for an arbitrary user". See
 * apps/api/src/routes/admin-users.ts for the batched, read-only Prisma
 * queries backing this.
 *
 * Listing/search and every mutation (role, ban/unban, session revocation,
 * delete) go straight through `authClient.admin.*` instead — this file is
 * deliberately narrow to just the enrichment gap.
 */
import { apiFetch } from "./client";

export interface AdminUserEnrichment {
  providers: string[];
  lastLoginAt: string | null;
}

export interface AdminUserEnrichmentResponse {
  users: Record<string, AdminUserEnrichment>;
}

export type PriceReadOutcome =
  | "memory_hit"
  | "shared_hit"
  | "coalesced_success"
  | "refresh_success"
  | "uncacheable_live"
  | "source_failure"
  | "failure_cooldown"
  | "uncacheable_cooldown"
  | "refresh_timeout"
  | "database_unavailable";

export type PriceCoordinationFact =
  "refresh_owner" | "coalesced_waiter_process" | "coalesced_waiter_database";

export type PriceProviderFact = "provider_429" | "quota_rejection";

export type PriceActivityTarget =
  | {
      source: "kroger";
      target: "products";
      activity: "product_search" | "product_detail" | "product_upc";
    }
  | {
      source: "kroger";
      target: "locations";
      activity: "location_search" | "location_detail";
    }
  | {
      source: "kroger";
      target: "identity";
      activity: "oauth_code_exchange" | "oauth_refresh" | "app_token";
    }
  | { source: "kroger"; target: "cart"; activity: "cart_add" }
  | {
      source: "bls";
      target: "national_prices";
      activity: "national_price_load";
    };

export type PriceActivityObservability = PriceActivityTarget & {
  readOutcomes: Record<PriceReadOutcome, string>;
  coordinationFacts: Record<PriceCoordinationFact, string>;
  providerFacts: Record<PriceProviderFact, string>;
  reuseRatio: number | null;
  sourceAvailability: number | null;
};

export type PriceSourceWarning = PriceActivityTarget & {
  kind:
    | "low_reuse"
    | "low_availability"
    | "provider_throttling"
    | "quota_exhaustion";
};

export interface PriceSourceObservabilityReport {
  window: { from: string; through: string; days: 7 };
  totals: {
    readOutcomes: Record<PriceReadOutcome, string>;
    coordinationFacts: Record<PriceCoordinationFact, string>;
    providerFacts: Record<PriceProviderFact, string>;
    reuseRatio: number | null;
    sourceAvailability: number | null;
  };
  byActivity: PriceActivityObservability[];
  days: Array<{
    day: string;
    byActivity: PriceActivityObservability[];
  }>;
  warnings: PriceSourceWarning[];
}

export interface AdminRetailerApiUsageResponse {
  today: string;
  usage: Array<{
    retailer: "kroger";
    api: "products" | "locations" | "identity" | "cart";
    used: number;
    limit: number;
    percent: number;
    warning: boolean;
    exhausted: boolean;
  }>;
  history: Array<{
    day: string;
    retailer: string;
    api: string;
    count: number;
  }>;
  priceObservability: PriceSourceObservabilityReport;
}

export async function fetchAdminUserEnrichment(
  userIds: string[],
): Promise<AdminUserEnrichmentResponse> {
  const params = new URLSearchParams({ userIds: userIds.join(",") });
  return apiFetch<AdminUserEnrichmentResponse>(
    `/admin/users/enrichment?${params.toString()}`,
  );
}

export async function fetchAdminRetailerApiUsage(): Promise<AdminRetailerApiUsageResponse> {
  return apiFetch<AdminRetailerApiUsageResponse>(
    "/admin/users/retailer-api-usage",
  );
}
